/**
 * This file serves as a compatibility layer for components still using the old redux-oidc approach.
 * It provides a userManager API but delegates the actual functionality to @axa-fr/react-oidc.
 * 
 * This allows for a smoother transition from redux-oidc to @axa-fr/react-oidc.
 * Code should be updated to use the new APIs directly when possible.
 */
import { oidcConfiguration } from '../../../components/auth/AuthConfig';

/**
 * Creates a proxy object that simulates the userManager API using sessionStorage
 * for minimal compatibility with code that might still expect the old API.
 */

// Event listeners
const userLoadedListeners = [];
const userUnloadedListeners = [];
const silentRenewErrorListeners = [];
const userSignedOutListeners = [];

const userManager = {
  // Return the current user from sessionStorage
  getUser: () => {
    const userJson = sessionStorage.getItem('user_profile');
    if (userJson) {
      try {
        return Promise.resolve(JSON.parse(userJson));
      } catch (e) {
        return Promise.resolve(null);
      }
    }
    return Promise.resolve(null);
  },
  
  // Redirects to login using the configuration
  signinRedirect: (args = {}) => {
    const { data, redirectUrl } = args;
    if (redirectUrl) {
      sessionStorage.setItem('returnUrl', redirectUrl);
    }
    if (data) {
      sessionStorage.setItem('oidc_state_data', JSON.stringify(data));
    }
    
    // Generate state for CSRF protection
    const state = Math.random().toString(36).substring(2);
    sessionStorage.setItem('oidc_state', state);
    
    // Use the @axa-fr/react-oidc configuration to redirect
    window.location.replace(`${oidcConfiguration.authority}/authorize?client_id=${oidcConfiguration.client_id}&redirect_uri=${encodeURIComponent(oidcConfiguration.redirect_uri)}&response_type=${oidcConfiguration.response_type}&scope=${encodeURIComponent(oidcConfiguration.scope)}&state=${state}`);
    return Promise.resolve();
  },
  
  // Redirects to logout using the configuration
  signoutRedirect: () => {
    sessionStorage.clear();
    window.location.replace(oidcConfiguration.post_logout_redirect_uri);
    return Promise.resolve();
  },
  
  // Signs out and removes user from storage
  removeUser: () => {
    sessionStorage.removeItem('user_profile');
    return Promise.resolve();
  },
  
  // Allows registering event callbacks
  events: {
    addUserLoaded: (callback) => userLoadedListeners.push(callback),
    addUserUnloaded: (callback) => userUnloadedListeners.push(callback),
    addSilentRenewError: (callback) => { silentRenewErrorListeners.push(callback); },
    addUserSignedOut: (callback) => { userSignedOutListeners.push(callback); }
  }
};

export default userManager;
